import { DestroyRef, effect, inject, Signal, untracked } from '@angular/core';
import { Subscription } from 'rxjs';
import { StateHolder } from '../services/state-holder';

export abstract class SignalHolder<S> extends StateHolder<S> {
  private readonly _destroyRef = inject(DestroyRef);

  protected constructor(initialState: S) {
    super(initialState);
  }

  /**
   * Return the store state as a (read-only) signal.
   */
  public get state$(): Signal<S> {
    return this.stateSignal;
  }

  /**
   * Create a computed signal, derived from the state and a given mapper function.
   */
  protected compute<R>(mapper: (state: S) => R): Signal<R> {
    return this.computeSignal(mapper);
  }

  /**
   * Create a computed signal using reference equality (Object.is) instead of deep equality.
   * Use for signals extracting primitive or enum values where deep comparison is unnecessary.
   */
  protected computeByRef<R>(mapper: (state: S) => R): Signal<R> {
    return this.computeSignalByRef(mapper);
  }

  /**
   * Syncs an Angular signal input to the component state.
   * Call in the constructor after super().
   *
   * Usage:
   *   public readonly table = input.required<SolTable<PermissionRow>>();
   *
   *   constructor() {
   *     super(MyState.of());
   *     this.syncInput(this.table, (state, table) => state.withTable(table));
   *   }
   */
  protected syncInput<T>(inputSignal: Signal<T>, updater: (state: S, value: T) => Partial<S>): void {
    effect(() => {
      const value = inputSignal();
      untracked(() => this.update(state => updater(state, value)));
    });
  }

  protected latestAction<A extends readonly unknown[]>(
    action: (...args: A) => Subscription | undefined,
  ): (...args: A) => void {
    let subscription: Subscription | undefined;
    this._destroyRef.onDestroy(() => subscription?.unsubscribe());

    return (...args: A): void => {
      subscription?.unsubscribe();
      subscription = action(...args);
    };
  }
}
